// Monte Carlo sobre a carteira de NDF exibida na aba de posicoes.
//
// Regra que governa este arquivo: a marcacao simulada usa AS MESMAS funcoes da
// aba de posicoes (`curveAt` + `nexusSideOf`). Nao e economia de codigo — e o
// que garante que o "MTM atual" daqui bate com o total da tabela ao lado. Uma
// segunda formula de marcacao daria dois numeros diferentes na mesma tela e
// ninguem saberia em qual acreditar.
//
// Um unico fator de risco: o spot de cada par. No nosso modelo de curva o
// carrego e deterministico, entao nao ha risco de cupom cambial — um contrato
// de 1 ano e um de 1 mes so diferem pela alavancagem do spot.

import { curveAt, nexusSideOf, pairConfig, CURVE_NODES_DU } from "@/utils/ndfPortfolio";

const CURVE_FLAT_SHAPE = CURVE_NODES_DU.map(() => 1);

// Volatilidade anualizada por par. Mockada, no mesmo lugar conceitual que os
// parametros da curva: quando o engine do Nexus entrar, entra por aqui.
export const DEFAULT_VOLS = {
  USDBRL: 0.14,
  EURBRL: 0.15,
  GBPBRL: 0.16,
  JPYBRL: 0.18,
};

const FALLBACK_VOL = 0.15;

// USDBRL, EURBRL e GBPBRL sao dominados pela perna BRL — andam quase juntos.
// Sem essa correlacao a carteira parece muito mais diversificada do que e, e o
// VaR sai subestimado.
const MAJOR_BRL = new Set(["USDBRL", "EURBRL", "GBPBRL"]);
const CORR_MAJORS = 0.85;
const CORR_CROSS = 0.55;

export const DU_PER_YEAR = 252;

// Acima disto o fan chart deixa de guardar trajetoria por caminho: os
// percentis ja convergiram e a memoria cresce por caminho x passo.
const MAX_FAN_PATHS = 5000;

// Tamanho minimo do lote entre um `yield` e outro, para a UI nao congelar. O
// lote CRESCE com o numero de caminhos: `setTimeout(0)` tem piso de ~4ms depois
// de algumas chamadas aninhadas, entao 500 yields custariam ~2s de puro
// overhead numa rodada de 1MM. Limitando a `MAX_YIELDS`, o custo fica em ~0,4s
// e a barra de progresso ainda anda de 1 em 1%.
const BATCH_PATHS = 2000;
const MAX_YIELDS = 100;

// Fracao da cauda usada no CVaR e na decomposicao por contrato.
const TAIL_P = 0.05;

/* ------------------------------------------------------------------ *
 * Aleatoriedade
 * ------------------------------------------------------------------ */

// PRNG semeado: rodar de novo com a mesma semente devolve o mesmo resultado.
// E o que torna o painel discutivel numa reuniao, em vez de um numero que muda
// a cada clique.
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeNormal(rand) {
  let spare = null;
  return function normal() {
    if (spare !== null) {
      const v = spare;
      spare = null;
      return v;
    }
    let u = 0;
    let v = 0;
    let s = 0;
    do {
      u = rand() * 2 - 1;
      v = rand() * 2 - 1;
      s = u * u + v * v;
    } while (s === 0 || s >= 1);
    const f = Math.sqrt((-2 * Math.log(s)) / s);
    spare = v * f;
    return u * f;
  };
}

/* ------------------------------------------------------------------ *
 * Correlacao
 * ------------------------------------------------------------------ */

export function correlationOf(a, b) {
  if (a === b) return 1;
  return MAJOR_BRL.has(a) && MAJOR_BRL.has(b) ? CORR_MAJORS : CORR_CROSS;
}

// Cholesky de uma matriz pequena (no maximo um punhado de pares).
export function cholesky(matrix) {
  const n = matrix.length;
  const L = Array.from({ length: n }, () => new Float64Array(n));
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j <= i; j += 1) {
      let sum = matrix[i][j];
      for (let k = 0; k < j; k += 1) sum -= L[i][k] * L[j][k];
      if (i === j) L[i][j] = Math.sqrt(Math.max(sum, 1e-12));
      else L[i][j] = sum / L[j][j];
    }
  }
  return L;
}

function correlationMatrix(pairs) {
  return pairs.map((a) => pairs.map((b) => correlationOf(a, b)));
}

/* ------------------------------------------------------------------ *
 * Estatistica
 * ------------------------------------------------------------------ */

// Percentil por interpolacao linear sobre array JA ORDENADO.
export function percentile(sorted, p) {
  const n = sorted.length;
  if (!n) return 0;
  const idx = (n - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

function histogram(values, bins = 41) {
  const n = values.length;
  if (!n) return [];
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < n; i += 1) {
    if (values[i] < min) min = values[i];
    if (values[i] > max) max = values[i];
  }
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const width = (max - min) / bins;
  const counts = new Array(bins).fill(0);
  for (let i = 0; i < n; i += 1) {
    let k = Math.floor((values[i] - min) / width);
    if (k < 0) k = 0;
    if (k >= bins) k = bins - 1;
    counts[k] += 1;
  }
  return counts.map((count, i) => ({
    x0: min + i * width,
    x1: min + (i + 1) * width,
    mid: min + (i + 0.5) * width,
    count,
    pct: count / n,
  }));
}

/* ------------------------------------------------------------------ *
 * Simulacao
 * ------------------------------------------------------------------ */

const yieldToUi = () => new Promise((resolve) => setTimeout(resolve, 0));

// Heap de capacidade fixa com os `k` PIORES caminhos, cada um carregando o
// vetor de P&L por contrato.
//
// A alternativa obvia — guardar a matriz caminho x contrato inteira e ordenar
// no fim — custa 72MB e uma ordenacao de 1MM de elementos com comparador numa
// rodada grande. Aqui a memoria e O(k x contratos) e o custo por caminho e
// O(log k), independente do numero de caminhos.
//
// E exato, nao amostral: como `k` e o mesmo corte usado no CVaR, a soma das
// contribuicoes continua batendo com o CVaR na casa decimal.
function makeTailHeap(capacity, width) {
  const keys = new Float64Array(capacity);
  const values = new Float64Array(capacity * width);
  let size = 0;

  const swap = (i, j) => {
    const k = keys[i];
    keys[i] = keys[j];
    keys[j] = k;
    const oi = i * width;
    const oj = j * width;
    for (let t = 0; t < width; t += 1) {
      const v = values[oi + t];
      values[oi + t] = values[oj + t];
      values[oj + t] = v;
    }
  };

  // Max-heap: a RAIZ e o menos ruim dos guardados. E contra ela que cada novo
  // caminho e comparado.
  const siftUp = (start) => {
    let i = start;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (keys[parent] >= keys[i]) break;
      swap(parent, i);
      i = parent;
    }
  };

  const siftDown = (start) => {
    let i = start;
    for (;;) {
      const left = 2 * i + 1;
      const right = left + 1;
      let largest = i;
      if (left < size && keys[left] > keys[largest]) largest = left;
      if (right < size && keys[right] > keys[largest]) largest = right;
      if (largest === i) break;
      swap(i, largest);
      i = largest;
    }
  };

  return {
    get size() {
      return size;
    },
    values,
    keys,
    offer(key, vector) {
      if (size < capacity) {
        keys[size] = key;
        values.set(vector, size * width);
        size += 1;
        siftUp(size - 1);
      } else if (key < keys[0]) {
        keys[0] = key;
        values.set(vector, 0);
        siftDown(0);
      }
    },
  };
}

// `rows` sao as linhas JA precificadas pelo store (trazem du, strike, notional
// e sinal). `curves` sao as curvas correntes — o formato (shape) delas e
// reaproveitado, so o spot e simulado.
export async function runMonteCarlo({
  rows = [],
  curves = {},
  horizonDu = 21,
  paths = 10000,
  volScale = 1,
  seed = null,
  vols = DEFAULT_VOLS,
  onProgress = null,
} = {}) {
  const n = rows.length;
  const H = Math.max(1, Math.round(horizonDu));
  // Caminhos em pares: cada sorteio gera tambem seu espelho antitetico, o que
  // corta o ruido do VaR pela metade sem custo nenhum.
  const totalPaths = Math.max(2, Math.round(paths / 2) * 2);
  const usedSeed = seed == null ? (Math.random() * 4294967296) >>> 0 : seed >>> 0;

  const mtm0 = rows.reduce((s, r) => s + r.mtm, 0);

  if (!n) {
    return emptyResult(usedSeed, totalPaths, H, mtm0);
  }

  const pairs = [...new Set(rows.map((r) => r.pair))];
  const pairIndex = new Map(pairs.map((p, i) => [p, i]));
  const nPairs = pairs.length;
  const L = cholesky(correlationMatrix(pairs));

  // Parametros por par. Drift = carrego, em medida neutra a risco: com isso
  // E[S_T] cai exatamente no forward, e a media do P&L simulado tem que voltar
  // proxima de zero. E o teste de sanidade embutido da simulacao.
  const spot0 = new Float64Array(nPairs);
  const drift = new Float64Array(nPairs);
  const stepVol = new Float64Array(nPairs);
  pairs.forEach((p, i) => {
    const cfg = pairConfig(p);
    const curve = curves[p];
    spot0[i] = curve ? curve.spot : cfg.spot;
    const sigma = (vols[p] != null ? vols[p] : FALLBACK_VOL) * volScale;
    stepVol[i] = sigma / Math.sqrt(DU_PER_YEAR);
    drift[i] = Math.log(1 + (curve ? curve.carry : cfg.carry)) / DU_PER_YEAR;
  });

  // Objeto de curva reutilizado a cada avaliacao: so o spot muda.
  const scratch = pairs.map((p) => ({
    pair: p,
    spot: spot0[pairIndex.get(p)],
    carry: curves[p] ? curves[p].carry : pairConfig(p).carry,
    // Sem curva viva o formato e plano — `curveAt` indexa `shape` sem checar.
    shape: (curves[p] && curves[p].shape) || CURVE_FLAT_SHAPE,
  }));

  const meta = rows.map((r) => ({
    pairIdx: pairIndex.get(r.pair),
    du: r.du,
    strike: r.strike,
    notional: r.notional,
    sign: r.sign,
    side: r.side,
    mtm0: r.mtm,
  }));

  const rand = mulberry32(usedSeed);
  const normal = makeNormal(rand);

  const pnl = new Float64Array(totalPaths);
  const tailCount = Math.max(1, Math.floor(totalPaths * TAIL_P));
  const tailHeap = makeTailHeap(tailCount, n);
  const contractScratch = new Float64Array(n);
  const fanPaths = Math.min(totalPaths, MAX_FAN_PATHS);
  const fan = new Float64Array((H + 1) * fanPaths);

  const S = new Float64Array(nPairs);
  const z = new Float64Array(H * nPairs);
  const e = new Float64Array(nPairs);
  const locked = new Float64Array(n);
  const settled = new Uint8Array(n);

  // Marca a carteira inteira num instante `t` do caminho. Contrato ja fixado
  // devolve o P&L travado; os demais sao remarcados no prazo que resta.
  const markPortfolio = (t, out, outOffset) => {
    let total = 0;
    for (let i = 0; i < n; i += 1) {
      const m = meta[i];
      let value;
      if (settled[i]) {
        value = locked[i];
      } else {
        const curve = scratch[m.pairIdx];
        const quote = curveAt(curve, m.du - t);
        value = (nexusSideOf(quote, m.side) - m.strike) * m.notional * m.sign;
      }
      if (out) out[outOffset + i] = value;
      total += value;
    }
    return total;
  };

  const simulatePath = (path, mirror) => {
    for (let i = 0; i < nPairs; i += 1) S[i] = spot0[i];
    settled.fill(0);
    // Contrato ja vencido nao volta a sortear: o P&L dele esta travado desde
    // antes da simulacao comecar.
    for (let i = 0; i < n; i += 1) {
      if (meta[i].du <= 0) {
        locked[i] = meta[i].mtm0;
        settled[i] = 1;
      }
    }

    const keepFan = path < fanPaths;
    if (keepFan) fan[path * (H + 1)] = mtm0;

    for (let t = 1; t <= H; t += 1) {
      // Choques correlacionados: e = L z.
      for (let i = 0; i < nPairs; i += 1) {
        let acc = 0;
        for (let k = 0; k <= i; k += 1) acc += L[i][k] * (mirror ? -z[(t - 1) * nPairs + k] : z[(t - 1) * nPairs + k]);
        e[i] = acc;
      }
      for (let i = 0; i < nPairs; i += 1) {
        const s = stepVol[i];
        S[i] *= Math.exp(drift[i] - 0.5 * s * s + s * e[i]);
        scratch[i].spot = S[i];
      }

      // Contrato que vence neste passo fixa na PTAX (sem bid/ask) e trava o
      // P&L. Nao e caso de canto: em 21du boa parte de uma carteira de hedge
      // ja liquidou.
      for (let i = 0; i < n; i += 1) {
        const m = meta[i];
        if (!settled[i] && m.du <= t) {
          locked[i] = (S[m.pairIdx] - m.strike) * m.notional * m.sign;
          settled[i] = 1;
        }
      }

      if (keepFan) fan[path * (H + 1) + t] = markPortfolio(t, null, 0);
    }

    const total = markPortfolio(H, contractScratch, 0);
    const pathPnl = total - mtm0;
    pnl[path] = pathPnl;
    tailHeap.offer(pathPnl, contractScratch);
  };

  const batchSize = Math.max(BATCH_PATHS, Math.ceil(totalPaths / MAX_YIELDS / 2) * 2);
  for (let start = 0; start < totalPaths; start += batchSize) {
    const end = Math.min(totalPaths, start + batchSize);
    for (let path = start; path < end; path += 2) {
      for (let k = 0; k < H * nPairs; k += 1) z[k] = normal();
      simulatePath(path, false);
      if (path + 1 < end) simulatePath(path + 1, true);
    }
    if (onProgress) onProgress(end / totalPaths);
    if (end < totalPaths) await yieldToUi();
  }

  return summarize({
    rows,
    meta,
    pnl,
    tailHeap,
    tailCount,
    fan,
    fanPaths,
    H,
    n,
    mtm0,
    seed: usedSeed,
    totalPaths,
    volScale,
  });
}

function summarize({ rows, meta, pnl, tailHeap, tailCount, fan, fanPaths, H, n, mtm0, seed, totalPaths, volScale }) {
  const sorted = Float64Array.from(pnl).sort();
  const mean = sorted.reduce((s, v) => s + v, 0) / totalPaths;
  let variance = 0;
  for (let i = 0; i < totalPaths; i += 1) variance += (pnl[i] - mean) ** 2;
  variance /= Math.max(1, totalPaths - 1);

  // VaR e CVaR reportados como PERDA positiva: "posso perder X".
  const q = (p) => percentile(sorted, p);
  const tailMean = (p) => {
    const cut = Math.max(1, Math.floor(totalPaths * p));
    let acc = 0;
    for (let i = 0; i < cut; i += 1) acc += sorted[i];
    return acc / cut;
  };

  const var95 = -q(TAIL_P);
  const var99 = -q(0.01);
  const cvar95 = -tailMean(TAIL_P);
  const cvar99 = -tailMean(0.01);

  let losses = 0;
  for (let i = 0; i < totalPaths; i += 1) if (pnl[i] < 0) losses += 1;

  // Contribuicao para o CVaR: media do P&L de cada contrato CONDICIONADA aos
  // 5% piores caminhos DA CARTEIRA. Responde "qual posicao me machuca quando
  // doi", que e diferente de "qual posicao e mais volatil".
  //
  // Os caminhos da cauda vieram do heap — nao ha matriz completa para ordenar.
  const contrib = new Float64Array(n);
  const tailSize = tailHeap.size;
  for (let k = 0; k < tailSize; k += 1) {
    const offset = k * n;
    for (let i = 0; i < n; i += 1) contrib[i] += tailHeap.values[offset + i] - meta[i].mtm0;
  }
  const contribTotal = contrib.reduce((s, v) => s + v, 0);

  const byContractOut = rows
    .map((r, i) => ({
      id: r.id || `${r.pair}-${r.maturity}-${r.side}`,
      pair: r.pair,
      side: r.side,
      maturity: r.maturity,
      counterparty: r.counterparty,
      du: r.du,
      mtm0: r.mtm,
      tailPnl: contrib[i] / tailSize,
      // Sinal preservado: um contrato pode AJUDAR no cenario ruim (hedge).
      sharePct: contribTotal ? (contrib[i] / contribTotal) * 100 : 0,
    }))
    .sort((a, b) => a.tailPnl - b.tailPnl);

  // Percentis passo a passo — a faixa que estreita em degraus conforme os
  // contratos liquidam.
  const fanOut = [];
  const slice = new Float64Array(fanPaths);
  for (let t = 0; t <= H; t += 1) {
    for (let path = 0; path < fanPaths; path += 1) slice[path] = fan[path * (H + 1) + t];
    const s = Float64Array.from(slice).sort();
    fanOut.push({
      step: t,
      p5: percentile(s, 0.05),
      p25: percentile(s, 0.25),
      p50: percentile(s, 0.5),
      p75: percentile(s, 0.75),
      p95: percentile(s, 0.95),
    });
  }

  return {
    seed,
    paths: totalPaths,
    horizonDu: H,
    volScale,
    contracts: n,
    mtm0,
    mean,
    stdDev: Math.sqrt(variance),
    // Erro padrao da media: da a escala do ruido de Monte Carlo, para nao ler
    // como sinal uma diferenca que e so numero de caminhos.
    standardError: Math.sqrt(variance / totalPaths),
    var95,
    var99,
    cvar95,
    cvar99,
    worst: sorted[0],
    best: sorted[totalPaths - 1],
    probLoss: losses / totalPaths,
    // `histogram` opera direto no Float64Array: converter para Array
    // custaria outra copia de 8MB numa rodada de 1MM.
    histogram: histogram(pnl),
    fan: fanOut,
    byContract: byContractOut,
    fanPaths,
    tailPaths: tailSize,
  };
}

function emptyResult(seed, paths, H, mtm0) {
  return {
    seed,
    paths,
    horizonDu: H,
    volScale: 1,
    contracts: 0,
    mtm0,
    mean: 0,
    stdDev: 0,
    standardError: 0,
    var95: 0,
    var99: 0,
    cvar95: 0,
    cvar99: 0,
    worst: 0,
    best: 0,
    probLoss: 0,
    histogram: [],
    fan: [],
    byContract: [],
    fanPaths: 0,
    tailPaths: 0,
  };
}
